import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Trophy, Loader2 } from "lucide-react";

const METRICS = [
  { key: "revenue", label: "Revenue" },
  { key: "advertisers", label: "Advertisers" },
  { key: "views", label: "Views" },
  { key: "clicks", label: "Clicks" },
  { key: "favorites", label: "Favorites" },
  { key: "routeOpens", label: "Routes" },
  { key: "engagement", label: "Engagement" },
];

export default function TopPerformingZones() {
  const [metric, setMetric] = useState("advertisers");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get("/admin/zones/top-performing", { params: { metric, limit: 5 } })
      .then(({ data }) => { if (live) setItems(data.items || []); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [metric]);

  const val = (z) => metric === "revenue" ? `$${(z.revenue || 0).toFixed(0)}`
    : metric === "advertisers" ? `${z.advertiserCount}` : `${(z[metric] || 0).toLocaleString()}`;

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="top-zones-widget">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center"><Trophy className="w-4.5 h-4.5 text-amber-400" /></div>
          <div>
            <h3 className="font-heading font-bold">Top performing zones</h3>
            <p className="text-xs text-muted-foreground">Views, clicks & engagement populate as the Rider app reports them.</p>
          </div>
        </div>
        <div className="flex gap-1 flex-wrap">
          {METRICS.map((m) => (
            <Button key={m.key} size="sm" variant={metric === m.key ? "default" : "outline"} onClick={() => setMetric(m.key)} data-testid={`top-zones-metric-${m.key}`}>{m.label}</Button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">No zones yet.</p>
      ) : (
        <div className="divide-y divide-border" data-testid="top-zones-list">
          {items.map((z, i) => (
            <div key={z.zoneId} className="py-2.5 flex items-center gap-3" data-testid={`top-zone-${z.zoneId}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-amber-500/20 text-amber-400" : "bg-muted text-muted-foreground"}`}>{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{z.zoneName || "Zone"}</div>
                <div className="text-xs text-muted-foreground">{z.state || "—"} · {z.status}</div>
              </div>
              <div className="font-heading font-bold text-primary">{val(z)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
